import {Action, getModule, Module, Mutation} from 'vuex-module-decorators'
import store from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import AlertModule from "@/store/modules/alert";
import {DateTime} from "luxon";
import DataExchangeFileDto = MdbsCore.DataExchangeFileDto;
import QueryShortDto = MdbsCore.QueryShortDto;
import FinalExportRequestDto = MdbsCore.FinalExportRequestDto;

const exportsClient = new MdbsCore.ExportClient();
const akromegalieExportsClient = new MdbsCore.AkromegalieExportClient();

@Module({namespaced: true, name: 'exports', store: store})
export default class ExportsModule extends BaseModule {

    private _statusExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _csvExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _screeningExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _changesExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _queriesExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _userQueriesExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _queriesOverviewExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _rewardExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();
    private _reconciliationExportsFiles: Array<DataExchangeFileDto> = new Array<MdbsCore.DataExchangeFileDto>();

    public get reconciliationExportsFiles(): Array<DataExchangeFileDto> {
        return this._reconciliationExportsFiles;
    }

    public get rewardExportsFiles(): Array<DataExchangeFileDto> {
        return this._rewardExportsFiles;
    }

    public get statusExportsFiles(): Array<DataExchangeFileDto> {
        return this._statusExportsFiles;
    }

    public get csvExportsFiles(): Array<DataExchangeFileDto> {
        return this._csvExportsFiles;
    }

    public get screeningExportsFiles(): Array<DataExchangeFileDto> {
        return this._screeningExportsFiles;
    }

    public get changesExportsFiles(): Array<DataExchangeFileDto> {
        return this._changesExportsFiles;
    }

    public get queriesExportsFiles(): Array<DataExchangeFileDto> {
        return this._queriesExportsFiles;
    }

    public get userQueriesExportsFiles(): Array<DataExchangeFileDto> {
        return this._userQueriesExportsFiles;
    }

    public get queriesOverviewExportsFiles(): Array<DataExchangeFileDto> {
        return this._queriesOverviewExportsFiles;
    }

    @Mutation
    setReconciliationExportFiles(rewardExportsFiles: Array<DataExchangeFileDto>) {
        this._reconciliationExportsFiles = rewardExportsFiles;
    }

    @Mutation
    setRewardExportFiles(rewardExportsFiles: Array<DataExchangeFileDto>) {
        this._rewardExportsFiles = rewardExportsFiles;
    }

    @Mutation
    setStatusExportFiles(statusExportsFiles: Array<DataExchangeFileDto>) {
        this._statusExportsFiles = statusExportsFiles;
    }

    @Mutation
    setCsvExportFiles(csvExportsFiles: Array<DataExchangeFileDto>) {
        this._csvExportsFiles = csvExportsFiles;
    }

    @Mutation
    setScreeningExportFiles(screeningExportsFiles: Array<DataExchangeFileDto>) {
        this._screeningExportsFiles = screeningExportsFiles;
    }

    @Mutation
    setChangesExportFiles(changesExportsFiles: Array<DataExchangeFileDto>) {
        this._changesExportsFiles = changesExportsFiles;
    }

    @Mutation
    setQueriesExportFiles(queriesExportsFiles: Array<DataExchangeFileDto>) {
        this._queriesExportsFiles = queriesExportsFiles;
    }

    @Mutation
    setUserQueriesExportFiles(userQueriesExportsFiles: Array<DataExchangeFileDto>) {
        this._userQueriesExportsFiles = userQueriesExportsFiles;
    }

    @Mutation
    setQueriesOverviewExportFiles(queriesOverviewExportsFiles: Array<DataExchangeFileDto>) {
        this._queriesOverviewExportsFiles = queriesOverviewExportsFiles;
    }

    @Action
    async generateCsvExport(payload: { studyId: string }) {
        const {studyId} = payload;

        await this.execute(async () => await exportsClient.getStudyCsvExport(studyId, true, false, "de")).catch();
    }

    @Action
    async generateScreeningExport(payload: { studyId: string, fromDate: Date, toDate: Date }) {
        const {studyId, fromDate, toDate} = payload;
        await this.execute(async () => await exportsClient.getSafetyScreeningExport(studyId, fromDate, toDate)).catch();
    }


    @Action
    async generateChangesExport(payload: { studyId: string }) {
        const {studyId} = payload;

        const todayMorning = DateTime.now()
            .setZone("Europe/Berlin")
            .set({hour: 4, minute: 0})
            .toJSDate();
        const yesterdayMorning = new Date(todayMorning.getTime());
        yesterdayMorning.setDate(yesterdayMorning.getDate() - 1);

        await this.execute(async () => await exportsClient.getChangesExport(studyId, todayMorning, yesterdayMorning)).catch();
    }

    private payload1: { studyId: string };

    @Action
    async generateQueriesExport(payload: { studyId: string }) {
        this.payload1 = payload;
        const {studyId} =
            this.payload1;
        await this.execute(() => exportsClient.getQueriesExport(studyId)).catch();
    }

    @Action
    async generateStatusExport(payload: { studyId: string }) {
        const {studyId} = payload;
        await this.execute(async () => await akromegalieExportsClient.getStatusExport(studyId)).catch();
    }

    @Action
    async generateUserQueriesExport(payload: { studyId: string }) {
        const {studyId} = payload;
        this.startLoading()
        try {
            return await exportsClient.getUserQueriesExport(studyId);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action
    async generateQueriesOverviewExport(payload: { studyId: string , queries: Array<QueryShortDto>}) {
        const {studyId, queries} = payload;
        this.startLoading()
        try {
            return await exportsClient.getQueriesOverviewExport(studyId, queries);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action
    async generateFinalExport(payload: { studyId: string , studyInstitutes: Array<string>}) {
        const {studyId, studyInstitutes} = payload;
        this.startLoading()
        try {
            return await exportsClient.getFinalExport(studyId,
                new FinalExportRequestDto({studyId: studyId, languageIdentifier: "de", studyInstitutes: studyInstitutes}));
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async getCsvExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getStudyCsvExportList(fromDate);

            this.setCsvExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getScreeningExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getSafetyScreeningExportList(fromDate);

            this.setScreeningExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getChangesExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getChangesExportList(fromDate);

            this.setChangesExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getQueriesExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getQueriesExportList(fromDate);

            this.setQueriesExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getUserQueriesExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getUserQueriesExportList(fromDate);

            this.setUserQueriesExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getQueriesOverviewExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await exportsClient.getQueriesOverviewExportList(fromDate);

            this.setQueriesOverviewExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async getStatusExportsFiles(payload: { fromDate: Date | undefined }) {
        const {fromDate} = payload;

        await this.execute(async () => {
            const files = await akromegalieExportsClient.getStatusExportList(fromDate);

            this.setStatusExportFiles(files);
        }).catch();
    }

    @Action({rawError: true})
    async downloadCsvExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getStudyCsvExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async downloadScreeningExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getSafetyScreeningExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async downloadChangesExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getChangesExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async downloadQueriesExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getQueriesExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async downloadUserQueriesExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getUserQueriesExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }


    @Action({rawError: true})
    async downloadQueriesOverviewExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await exportsClient.getQueriesOverviewExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

    @Action({rawError: true})
    async downloadStatusExport(payload: { fileName: string }) {
        this.startLoading()
        const {fileName} = payload;

        try {
            return await akromegalieExportsClient.getStatusExportFile(fileName);
        } catch (e) {
            const alertModule = getModule(AlertModule, store);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading()
        }
    }

}
